import React from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import InlineLinkEntryCard from "../common/InlineLinkEntryCard";
import {
  PILL_BUTTON_BG,
  pillButtonBaseSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../../../utils/pillButtonStyles";

const PURPLE = PILL_BUTTON_BG;
const PURPLE_HOVER = PILL_BUTTON_BG;
const SOFT_BG = "#F7F5FF";
const SOFT_BORDER = "#EAE5FF";

function PillButton({ sx, ...props }) {
  return (
    <Button
      {...props}
      className={withPillButtonClassName(props.className)}
      sx={[pillButtonBaseSx, pillButtonInteractiveSx, { fontWeight: 400, px: 2.1 }, sx || {}]}
    />
  );
}

function ChoiceRow({ icon, title, subtitle, onClick }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 2.5,
        bgcolor: "#fff",
        p: 1.5,
        cursor: "pointer",
        "&:hover": { boxShadow: "0 10px 28px rgba(124,58,237,0.12)" },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: SOFT_BG,
            border: `1px solid ${SOFT_BORDER}`,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280", mt: 0.25 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function UploadChoiceRow({ onPickFile, disabled, parsingResume }) {
  return (
    <Box
      sx={{
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 2.5,
        bgcolor: SOFT_BG,
        p: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "#fff",
            border: `1px solid ${SOFT_BORDER}`,
          }}
        >
          <UploadFileIcon sx={{ fontSize: 18, color: PURPLE }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            Upload Resume
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280", mt: 0.25 }}>
            Upload PDF/DOC/DOCX to autofill fields
          </Typography>
        </Box>

        <PillButton
          component="label"
          variant="contained"
          disabled={disabled}
          sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_HOVER } }}
          startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
        >
          {parsingResume ? "Parsing..." : "Resume"}
          <input
            hidden
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={onPickFile}
          />
        </PillButton>
      </Stack>
    </Box>
  );
}

export default function CandidateEntryOptions({
  onStartFromScratch,
  onLinkChange,
  onLinkContinue,
  onPickUploadFile,
  linkValue = "",
  linkHelperText = "",
  linkErrorText = "",
  linkLoading = false,
  uploadDisabled = false,
  parsingResume = false,
}) {
  return (
    <Box sx={{ p: 4 }}>
      <Stack spacing={1.25}>
        <ChoiceRow
          icon={<PersonOutlineIcon sx={{ fontSize: 18, color: PURPLE }} />}
          title="Start from Scratch"
          subtitle="Manually enter candidate information"
          onClick={onStartFromScratch}
        />

        <InlineLinkEntryCard
          title="Use Link"
          subtitle="Paste a public profile link"
          value={linkValue}
          onChange={onLinkChange}
          onSubmit={onLinkContinue}
          helperText={linkHelperText}
          errorText={linkErrorText}
          loading={linkLoading}
        />

        <UploadChoiceRow
          onPickFile={onPickUploadFile}
          disabled={uploadDisabled}
          parsingResume={parsingResume}
        />

      </Stack>
    </Box>
  );
}
